import React from 'react';
import { LinkifyText } from './LinkifyText';
import { stickerTextStyle } from './StickerText';

interface FormattedTextProps {
    text: string;
    onNavigateToProfile?: (userId: string) => void;
    onNavigateToHashtag?: (tag: string) => void;
    textSize?: 'xs' | 'sm' | 'med' | 'lg';
    fontSize?: number;
    textColor?: string;
    fontFamily?: string;
    /** When true, applies the sticker text effect (black outline + white glow). */
    stickerize?: boolean;
}

export const FormattedText = ({
    text,
    onNavigateToProfile,
    onNavigateToHashtag,
    textSize = 'med',
    fontSize,
    textColor,
    fontFamily,
    stickerize = false
}: FormattedTextProps) => {

    if (!text) return null;

    const stickerStyle = stickerize ? stickerTextStyle(0.35, textColor) : {};

    // Only match @mentions and #hashtags when preceded by whitespace or at start of string
    // This prevents mid-word @ (e.g. "llkjsdf@lakdjfda") from being treated as mentions
    // and lets emails (e.g. "text-before.the@domain.com") pass through to LinkifyText
    const tokenRegex = /(^|[^a-zA-Z0-9])([#@])([a-zA-Z0-9_\-]+(?:\.[a-zA-Z0-9_\-]+)*)/g;

    const sizeClasses = {
        xs: 'text-xs leading-tight',
        sm: 'text-base leading-snug',
        med: 'text-xl font-medium',
        lg: 'text-3xl font-bold'
    };

    const renderedParts: React.ReactNode[] = [];
    let lastIndex = 0;
    let keyIdx = 0;
    let match;

    while ((match = tokenRegex.exec(text)) !== null) {
        const [fullMatch, space, prefix, value] = match;
        const matchStart = match.index + space.length;

        // Add any text before this match as LinkifyText
        if (matchStart > lastIndex) {
            renderedParts.push(<LinkifyText key={keyIdx++} text={text.slice(lastIndex, matchStart)} />);
        }

        if (prefix === '#') {
            renderedParts.push(
                <button
                    key={keyIdx++}
                    onClick={(e) => { e.stopPropagation(); onNavigateToHashtag?.(value); }}
                    className="text-blue-400 hover:underline font-semibold text-raised"
                    style={stickerize ? stickerStyle : undefined}
                >
                    #{value}
                </button>
            );
        } else if (prefix === '@') {
            renderedParts.push(
                <button
                    key={keyIdx++}
                    onClick={(e) => { e.stopPropagation(); onNavigateToProfile?.(value); }}
                    className="text-white hover:underline font-bold text-blue-400 text-raised"
                    style={{ color: '#60a5fa', ...stickerStyle }}
                >
                    @{value}
                </button>
            );
        }

        lastIndex = matchStart + (fullMatch.length - space.length);
    }

    // Add remaining text after last match
    if (lastIndex < text.length) {
        renderedParts.push(<LinkifyText key={keyIdx++} text={text.slice(lastIndex)} />);
    } else if (lastIndex === 0) {
        // No matches at all — pass entire text to LinkifyText
        renderedParts.push(<LinkifyText key={0} text={text} />);
    }

    return (
        <span 
            className={`${!fontSize ? sizeClasses[textSize] : ''} whitespace-pre-wrap`}
            style={{ 
                ...(fontSize ? { fontSize: `${fontSize}px` } : {}),
                ...(textColor ? { color: textColor } : {}),
                ...(fontFamily ? { fontFamily } : {}),
                ...stickerStyle
            }}
        >
            {renderedParts}
        </span>
    );
};

